import * as React from 'react'

import { Reducer, UnknownAction } from '@reduxjs/toolkit'
import {
  access,
  Translations,
  useGlotstack,
  LocaleRegion,
  extractSimplePlaceholders,
  ParsedSimplePlaceholder,
  renderPlaceholdersToNodes,
} from 'glotstack'
import yaml from 'js-yaml'
import { without } from 'lodash'
import merge from 'lodash/merge'
import { type ParsedQuery } from 'query-string'

function pickRandom<T>(arr: T[], n: number): T[] {
  if (n >= arr.length) {
    return [...arr]
  }

  const result: T[] = []
  const usedIndices = new Set<number>()

  while (result.length < n) {
    const index = Math.floor(Math.random() * arr.length)
    if (!usedIndices.has(index)) {
      usedIndices.add(index)
      result.push(arr[index])
    }
  }

  return result
}

import {
  AppDispatch,
  AppGetState,
  AppState,
  useSelector,
} from 'domains/redux/store'

export enum ClientSize {
  XL = 0,
  DESKTOP = 1,
  LAPTOP = 2,
  TABLET = 3,
  MOBILE = 4,
}
export type JSONValue =
  | string
  | number
  | boolean
  | null
  | JSONArray
  | JSONObject

export interface JSONObject {
  [key: string]: JSONValue
}

export interface JSONArray extends Array<JSONValue> {}
const ALL_LOCALES = [
  'en-US',
  'fr-FR',
  'de-DE',
  'nl-NL',
  'jp-JP',
  'zh-CN',
  'pl-PL',
  'sr-SR',
  'zu-ZA',
  'nb-NO',
  'yue-HK',
  'hi-IN',
  'lv-LV',
  'th-TH',
]

interface ApplicationState {
  path: string
  canGoBack: boolean
  previousLocation: string | null
  isServer: boolean
  urlArgs: ParsedQuery
  snacks: { message: string }[]
  translations: Record<LocaleRegion, Translations> | null
  locale: LocaleRegion
  randomLocale: LocaleRegion
  locales: LocaleRegion[]
  preview?: { source: string; translations: string }
  clientSize: ClientSize
  apiKey: string
  cfSiteKey: string | null
  demo: {
    configText: string
    config: JSONObject
    translationsText: string
    translations: Translations
    fetching: boolean
    locale: string
    open: boolean
    allTranslations: Record<LocaleRegion, Translations>
    error: string | null
  }
}

export const DEFAULT_STATE = {
  path: '/',
  canGoBack: false,
  previousLocation: null,
  isServer: false,
  urlArgs: {},
  snacks: [],
  cfSiteKey: null,
  translations: null,
  locale: 'en-US' as LocaleRegion,
  randomLocale: 'de-DE' as LocaleRegion,
  clientSize: ClientSize.DESKTOP,
  apiKey: '<uninitialized>',
  locales: ALL_LOCALES as LocaleRegion[],
  demo: {
    configText: '',
    config: {
      outputLocales: pickRandom(without(ALL_LOCALES, 'en-US'), 1),
      apiKey: '<uninitializad>',
      projectId: 'glotstack',
      usage: {
        context:
          'SynchroChat is a real-time message platform like Slack or Discord.\n',
        properNouns: ['React', 'SynchroChat'],
      },
    },
    translationsText: '',
    translations: {
      Home: {
        Hero: {
          title: {
            value: 'Welcome to SynchroChat! The next best chat app.',
            context: 'The header for the homepage of the app',
          },
          subtitle: {
            value:
              "Synchronize all your chats on one platform. Chat \nand live your life Sync'd!\n",
            context: 'The subheader for the homepage of the app.',
          },
        },
        'demo-title': {
          value: '<Comp>Demo!</Comp>',
          context: 'The title of a section of the homepage demoing the app',
        },
      },
    },
    open: false,
    fetching: false,
    locale: 'en-US',
    allTranslations: {},
    error: null,
  },
}

interface SetPathAction extends UnknownAction {
  type: 'application/setPath'
  payload: {
    path: string
    queryArgs?: Record<string, string | string[]>
    pathArgs?: Record<string, string>
    args?: SetPathAction['payload']['queryArgs'] &
      SetPathAction['payload']['pathArgs']
  }
}

interface UpdateTranslationsAction extends UnknownAction {
  type: 'application/updateTranslations'
  payload: Record<LocaleRegion, Translations>
}

interface SetLocaleAction extends UnknownAction {
  type: 'application/setLocale'
  payload: { locale: LocaleRegion }
}

interface DemoAction extends UnknownAction {
  type: 'application/demoAction'
  payload: {
    configText?: string
    translationsText?: string
    fetching?: boolean
    locale?: string
    open?: boolean
    allTranslations?: Record<string, Translations>
    error?: string
  }
}

interface SetClientSize extends UnknownAction {
  type: 'application/setClientSize'
  payload: ClientSize
}

export type ApplicationActions =
  | SetPathAction
  | UpdateTranslationsAction
  | SetLocaleAction
  | SetClientSize
  | DemoAction

export const applicationReducer: Reducer<
  ApplicationState,
  ApplicationActions
> = (state = DEFAULT_STATE, action) => {
  switch (action.type) {
    case 'application/setPath': {
      return {
        ...state,
        ...(action as SetPathAction).payload,
      }
    }

    case 'application/updateTranslations': {
      return {
        ...state,
        translations: merge({}, state.translations, merge({}, action.payload)),
      }
    }

    case 'application/setLocale': {
      const filtered = state.locales.filter((x) => x !== action.payload.locale)
      const randomLocale = filtered[Math.floor(Math.random() * filtered.length)]

      return {
        ...state,
        locale: action.payload.locale,
        randomLocale,
      }
    }

    case 'application/setClientSize': {
      return { ...state, clientSize: action.payload }
    }

    case 'application/demoAction': {
      let newState = { ...state }
      let demo = { ...newState.demo }
      if (action.payload.fetching != undefined) {
        demo = { ...demo, fetching: action.payload.fetching }
      }
      if (action.payload.locale != undefined) {
        demo = { ...demo, locale: action.payload.locale }
      }
      if (action.payload.open != undefined) {
        demo = { ...demo, open: action.payload.open }
      }
      if (action.payload.allTranslations != undefined) {
        demo = { ...demo, allTranslations: action.payload.allTranslations }
      }
      if (action.payload.configText != undefined) {
        try {
          const config = yaml.load(action.payload.configText) as JSONObject
          demo = { ...demo, config: config }
        } catch (err) {
          // pass
        }
        demo = { ...demo, configText: action.payload.configText }
      }
      if (action.payload.translationsText != undefined) {
        try {
          const translations = yaml.load(
            action.payload.translationsText,
          ) as Translations
          demo = { ...demo, translations: translations }
        } catch (err) {
          // pass
        }
        demo = { ...demo, translationsText: action.payload.translationsText }
      }
      if (action.payload.error != undefined) {
        demo = { ...demo, error: action.payload.error }
      }

      newState.demo = demo
      return newState
    }

    default:
      return state
  }
}

export const selectTranslations = (state: AppState) =>
  state.application.translations
export const selectCurrentTranslations = (state: AppState) =>
  state.application.translations?.[state.application.locale]

export const selectLocale = (state: AppState) => state.application.locale
export const selectLocales = (state: AppState) => state.application.locales

interface TranslateOptsBase {
  locale?: LocaleRegion
  assigns?: Record<string, React.ReactNode>
}

export type TranslateFn = {
  (key: string, opts: TranslateOptsBase & { asString: true }): string
  (
    key: string,
    opts?: TranslateOptsBase & { asString?: false },
  ): React.ReactNode
}
function isAsStringTrue(
  opts: (TranslateOptsBase & { asString?: boolean }) | undefined,
): opts is TranslateOptsBase & { asString: true } {
  return opts?.asString === true
}

function translate(
  key: string,
  opts: {
    locale?: LocaleRegion
    assigns?: Record<string, React.ReactNode>
    asString?: boolean
    glotstack: ReturnType<typeof useGlotstack>
    globalLocale: LocaleRegion
    translations: Record<LocaleRegion, Translations>
    localeRef: React.MutableRefObject<LocaleRegion>
    accessedRef: React.MutableRefObject<Record<string, Record<string, string>>>
    extractionsRef: React.MutableRefObject<
      Record<
        string,
        Record<string, ParsedSimplePlaceholder[] | undefined> | undefined
      >
    >
    outputRef: React.MutableRefObject<
      Record<string, Record<string, React.ReactNode | undefined> | undefined>
    >
  },
): string | React.ReactNode {
  const {
    glotstack,
    globalLocale,
    translations,
    localeRef,
    accessedRef,
    extractionsRef,
    outputRef,
  } = opts
  const locale = opts?.locale ?? globalLocale
  localeRef.current = locale
  glotstack.loadTranslations(localeRef.current)

  let string = ''
  if (translations != null) {
    string = access(key, locale, translations ?? {})
  }

  if (string === key) return key

  if (
    outputRef.current[locale]?.[key] != null &&
    string === accessedRef.current[locale]?.[key]
  ) {
    return outputRef.current[locale][key]
  }

  accessedRef.current[locale] ??= {}
  accessedRef.current[locale][key] = string

  extractionsRef.current[locale] ??= {}
  if (!extractionsRef.current[locale][key]) {
    const newExtractions = extractSimplePlaceholders(string)
    extractionsRef.current[locale][key] = newExtractions
  }

  outputRef.current[locale] ??= {}
  if (outputRef.current[locale][key] == null) {
    const output = renderPlaceholdersToNodes(
      string,
      extractionsRef.current[locale][key],
      opts?.assigns ?? {},
    )
    outputRef.current[locale][key] = output
  }

  const output = outputRef.current[locale][key]

  if (isAsStringTrue(opts)) {
    // Convert any possible value to string safely
    return Array.isArray(output)
      ? output.join('')
      : typeof output === 'string'
        ? output
        : String(output ?? '')
  }
  return output as React.ReactNode
}

function createTranslate(
  glotstack: ReturnType<typeof useGlotstack>,
  globalLocale: LocaleRegion,
  translations: Record<LocaleRegion, Translations>,
  localeRef: React.MutableRefObject<LocaleRegion>,
  accessedRef: React.MutableRefObject<Record<string, Record<string, string>>>,
  extractionsRef: React.MutableRefObject<
    Record<
      string,
      Record<string, ParsedSimplePlaceholder[] | undefined> | undefined
    >
  >,
  outputRef: React.MutableRefObject<
    Record<string, Record<string, React.ReactNode | undefined> | undefined>
  >,
): TranslateFn {
  function t(
    key: string,
    opts?: {
      locale?: LocaleRegion
      assigns?: Record<string, React.ReactNode>
      asString: true
    },
  ): string
  function t(
    key: string,
    opts?: {
      locale?: LocaleRegion
      assigns?: Record<string, React.ReactNode>
      asString?: false
    },
  ): React.ReactNode
  function t(
    key: string,
    opts?: {
      locale?: LocaleRegion
      assigns?: Record<string, React.ReactNode>
      asString?: boolean
    },
  ) {
    return translate(
      key,
      merge(
        {
          glotstack,
          globalLocale,
          translations,
          localeRef,
          accessedRef,
          extractionsRef,
          outputRef,
        },
        opts,
      ),
    )
  }

  return t
}

export const useReduxGlotstack = () => {
  const glotstack = useGlotstack()
  const globalLocale = useSelector(selectLocale)
  const localeRef = React.useRef(globalLocale)
  localeRef.current = globalLocale

  const translations = useSelector(selectTranslations)

  React.useEffect(() => {
    glotstack.loadTranslations(localeRef.current)
  }, [localeRef.current])

  const accessedRef = React.useRef<Record<string, Record<string, string>>>({})
  const extractionsRef = React.useRef<
    Record<string, Record<string, ParsedSimplePlaceholder[]>>
  >({})
  const outputRef = React.useRef<
    Record<string, Record<string, React.ReactNode>>
  >({})

  const t: TranslateFn = React.useMemo(() => {
    return createTranslate(
      glotstack,
      globalLocale,
      translations ?? {},
      localeRef,
      accessedRef,
      extractionsRef,
      outputRef,
    )
  }, [globalLocale, translations])

  return { t }
}

export const fetchTranslations = (mountRef: React.MutableRefObject<HTMLElement>) => {
  return async (dispatch: AppDispatch, getState: AppGetState) => {
    if (getState().application.demo.fetching) {
      return
    }

    const { translations, config } = getState().application.demo

    const locales = config.outputLocales
    const usage = config.usage

    dispatch({ type: 'application/demoAction', payload: { fetching: true } })

    const siteKey = getState().application.cfSiteKey

    try {
      const token = await new Promise((resolve, reject) => {
        if (siteKey == null) {
          throw new Error('cannot validate API call')
        }
        const widgetId = window.turnstile.render(mountRef.current, {
          sitekey: siteKey,
          // sitekey: '1x00000000000000000000BB', // always allow
          // sitekey: '2x00000000000000000000AB', // always block
          size: 'compact',
          retry: 'never',
          theme: 'light',
          callback: (token) => {
            window.turnstile.remove(widgetId)
            resolve(token)
          },
          'error-callback': () => {
            reject(new Error('Cloudflare thinks you are suspicious!'))
          },
          'expired-callback': () => {
            console.warn('Turnstile expired')
          },
        })
      })

      const payload = {
        locales,
        usage,
        translations,
        nonce: token,
      }

      const apiKey = getState().application.demo.config.apiKey

      const response = await fetch('/api/translations', {
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${apiKey}`,
        },
        method: 'POST',
        body: JSON.stringify(payload),
      })

      console.info(response)
      if (response.status >= 400 && response.status < 600) {
        let statusText
        switch(response.status) {
          case 429:
            statusText = 'Too many requests'
            break;
          default:
            statusText = 'Unknown'
        }
        throw new Error(statusText)
      }

      const data = await response.json()

      dispatch({
        type: 'application/demoAction',
        payload: {
          fetching: false,
          open: true,
          allTranslations: data.data,
        },
      })
    } catch (err) {
      dispatch({
        type: 'application/demoAction',
        payload: { fetching: false, error: (err as Error).message },
      })
    }
  }
}
